import React from 'react';
import { Loader2 } from 'lucide-react';

/** Shared control heights — keep in sync with Input / CustomSelect / SearchSelect / product-theme.css */
const CONTROL_HEIGHT = {
  xs: 'h-7 min-h-7',
  sm: 'h-7 min-h-7',
  md: 'h-8 min-h-8',
  lg: 'h-9 min-h-9',
  xl: 'h-10 min-h-10',
};

const Button = ({
  children,
  type = 'button',
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  icon = null,
  iconPosition = 'left',
  fullWidth = false,
  className = '',
  onClick = () => {},
  ...props
}) => {
  const baseClasses =
    'inline-flex items-center justify-center box-border font-medium rounded-md border border-transparent shadow-none focus:outline-none focus:ring-1 focus:ring-offset-1 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses = {
    primary: 'bg-ink text-white hover:bg-neutral-800 focus:ring-ink',
    secondary: 'bg-canvas text-ink border-border hover:bg-surface-muted focus:ring-ink',
    outline: 'bg-transparent border-border text-ink hover:bg-surface-muted focus:ring-ink',
    ghost: 'bg-transparent border-transparent text-ink hover:bg-surface-muted focus:ring-ink',
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500',
    warning: 'bg-yellow-600 text-white hover:bg-yellow-700 focus:ring-yellow-500',
    info: 'bg-neutral-700 text-white hover:bg-neutral-800 focus:ring-neutral-600',
    link: 'h-auto min-h-0 bg-transparent border-transparent text-ink underline hover:text-ink-secondary focus:ring-ink',
  };

  const sizeClasses = {
    xs: `${CONTROL_HEIGHT.xs} px-2 text-[11px] leading-none`,
    sm: `${CONTROL_HEIGHT.sm} px-2.5 text-xs leading-none`,
    md: `${CONTROL_HEIGHT.md} px-3 text-xs leading-none`,
    lg: `${CONTROL_HEIGHT.lg} px-3.5 text-sm leading-none`,
    xl: `${CONTROL_HEIGHT.xl} px-4 text-sm leading-none`,
  };

  const iconSizeClasses = {
    xs: 'w-3 h-3',
    sm: 'w-3.5 h-3.5',
    md: 'w-3.5 h-3.5',
    lg: 'w-4 h-4',
    xl: 'w-4 h-4',
  };

  const buttonClasses = `
    ${baseClasses}
    ${variantClasses[variant] || variantClasses.primary}
    ${variant === 'link' ? 'px-0 text-xs' : sizeClasses[size] || sizeClasses.md}
    ${fullWidth ? 'w-full' : ''}
    ${className}
  `.trim();

  const iconSize = iconSizeClasses[size] || iconSizeClasses.md;

  const renderIcon = () => {
    if (loading) {
      return <Loader2 className={`${iconSize} animate-spin`} />;
    }
    if (icon) {
      const IconComponent = icon;
      return <IconComponent className={iconSize} />;
    }
    return null;
  };

  const renderContent = () => {
    const iconElement = renderIcon();
    if (!iconElement) return children;

    const gap = size === 'xs' || size === 'sm' ? 'gap-1.5' : 'gap-1.5';
    if (iconPosition === 'left') {
      return (
        <span className={`inline-flex items-center ${gap}`}>
          {iconElement}
          {children ? <span>{children}</span> : null}
        </span>
      );
    }
    return (
      <span className={`inline-flex items-center ${gap}`}>
        {children ? <span>{children}</span> : null}
        {iconElement}
      </span>
    );
  };

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={buttonClasses}
      {...props}
    >
      {renderContent()}
    </button>
  );
};

export default Button;
